import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { PaymentDto } from '../types/dtos';
import {
  CreditCard,
  Search,
  Download,
  Smartphone,
  CheckCircle2,
  Play,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  Send,
  Zap
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  coreInsuranceApi,
  GatewayStatusResponseDto,
  GatewayCheckoutResultDto,
  GatewayWebhookSimulationResultDto
} from '../api/coreInsuranceApi';

export default function Payments() {
  const [mainTab, setMainTab] = useState<'ledger' | 'sandbox'>('ledger');
  const [payments, setPayments] = useState<PaymentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeStatusFilter, setActiveStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Sandbox Console State
  const [gatewayStatuses, setGatewayStatuses] = useState<GatewayStatusResponseDto | null>(null);
  const [loadingGateways, setLoadingGateways] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<number>(1); // 1=PayChangu, 2=AirtelMoney, 3=TnmMpamba
  const [testPhone, setTestPhone] = useState('+265991887766');
  const [testAmount, setTestAmount] = useState<number>(750000);
  const [testPolicyNumber, setTestPolicyNumber] = useState('POL-MOT-2026-AUT-101');
  const [initiatingCheckout, setInitiatingCheckout] = useState(false);
  const [simulatingWebhook, setSimulatingWebhook] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState<GatewayCheckoutResultDto | null>(null);
  const [webhookResult, setWebhookResult] = useState<GatewayWebhookSimulationResultDto | null>(null);
  const [verificationResult, setVerificationResult] = useState<any>(null);

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<PaymentDto[]>('/admin/payments');
      setPayments(res.data);
    } catch (err: any) {
      console.error('[Payments] Failed to load payments:', err);
      setError(err.response?.data?.message || 'Unable to load payments.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGatewayStatuses = async () => {
    setLoadingGateways(true);
    try {
      const data = await coreInsuranceApi.getPaymentGatewayStatuses();
      setGatewayStatuses(data);
    } catch (err) {
      console.error('[Payments] Failed to load gateway status:', err);
    } finally {
      setLoadingGateways(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchGatewayStatuses();
  }, []);

  const handleProviderChange = (providerId: number) => {
    setSelectedProvider(providerId);
    if (providerId === 2) {
      setTestPhone('+265991887766');
    } else if (providerId === 3) {
      setTestPhone('+265888771122');
    } else {
      setTestPhone('+265991234567');
    }
    setCheckoutResult(null);
    setWebhookResult(null);
    setVerificationResult(null);
  };

  const handleInitiateCheckout = async () => {
    setInitiatingCheckout(true);
    setWebhookResult(null);
    setVerificationResult(null);
    try {
      const result = await coreInsuranceApi.testGatewayCheckout({
        policyId: '00000000-0000-0000-0000-000000000000',
        policyNumber: testPolicyNumber,
        policyholderName: 'Test Policyholder',
        policyholderEmail: 'customer@xtrem.mw',
        policyholderPhone: testPhone,
        amount: testAmount,
        provider: selectedProvider
      });

      setCheckoutResult(result);
      if (result.success) {
        toast.success(result.ussdPromptSent ? 'USSD Prompt dispatched!' : 'Checkout session generated!');
      } else {
        toast.error(result.ussdPromptMessage || 'Checkout failed.');
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to initiate gateway checkout.');
    } finally {
      setInitiatingCheckout(false);
    }
  };

  const handleSimulateWebhook = async () => {
    setSimulatingWebhook(true);
    try {
      const txRef = checkoutResult?.transactionReference || `TX-${testPolicyNumber}-SIM`;
      const result = await coreInsuranceApi.testGatewayWebhook({
        provider: selectedProvider,
        transactionReference: txRef,
        amount: testAmount,
        phoneNumber: testPhone,
        status: 'success'
      });

      setWebhookResult(result);
      toast.success(result.message || 'Webhook successfully simulated & policy bound!');
      fetchPayments(); // Refresh ledger
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to simulate webhook.');
    } finally {
      setSimulatingWebhook(false);
    }
  };

  const handleVerifyTransaction = async () => {
    setVerifyingPayment(true);
    try {
      const txRef = checkoutResult?.transactionReference || `TX-${testPolicyNumber}-SIM`;
      const result = await coreInsuranceApi.verifyGatewayTransaction(selectedProvider, txRef, testAmount);
      setVerificationResult(result);
      toast.success('Transaction status verified with gateway!');
    } catch (err: any) {
      console.error(err);
      toast.error('Failed to verify transaction status.');
    } finally {
      setVerifyingPayment(false);
    }
  };

  const filteredPayments = payments.filter(p => {
    const matchesTab = activeStatusFilter === 'All' || p.status === activeStatusFilter;
    const matchesSearch = p.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.policy.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  return (
    <div className="w-full space-y-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <CreditCard className="text-purple-600 dark:text-purple-400" />
              Payments & Mobile Money Gateway Ecosystem
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Real-time collections, PayChangu hosted checkout, Airtel Money USSD push, and TNM Mpamba STK prompt reconciliation.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                fetchPayments();
                fetchGatewayStatuses();
              }}
              className="p-2 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition"
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
            <button
              onClick={() => toast.success('Exporting payment ledger to CSV...')}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 shadow-sm transition"
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-gray-200 dark:border-slate-800 gap-4 mb-6">
          <button
            onClick={() => setMainTab('ledger')}
            className={`flex items-center gap-2 py-3 px-4 border-b-2 font-medium text-sm transition ${
              mainTab === 'ledger'
                ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <CreditCard size={18} />
            Transaction Ledger
          </button>
          <button
            onClick={() => setMainTab('sandbox')}
            className={`flex items-center gap-2 py-3 px-4 border-b-2 font-medium text-sm transition ${
              mainTab === 'sandbox'
                ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Zap size={18} />
            Live Gateway Sandbox & Webhook Simulator
          </button>
        </div>

        {/* TAB 1: LEDGER */}
        {mainTab === 'ledger' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm">
                <div className="text-[10px] font-bold text-gray-400 uppercase mb-1">Total Collected</div>
                <div className="text-lg sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400 truncate">
                  MK {payments.filter(p => p.status === 'Paid').reduce((sum, p) => sum + Number(p.amount.replace(/[^0-9.]/g, '')), 0).toLocaleString()}
                </div>
                <span className="text-xs text-emerald-600 font-medium">Reconciled in general ledger</span>
              </div>
              <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm">
                <div className="text-[10px] font-bold text-gray-400 uppercase mb-1">Pending Balance</div>
                <div className="text-lg sm:text-2xl font-bold text-amber-600 dark:text-amber-400 truncate">
                  MK {payments.filter(p => p.status === 'Pending').reduce((sum, p) => sum + Number(p.amount.replace(/[^0-9.]/g, '')), 0).toLocaleString()}
                </div>
                <span className="text-xs text-amber-500 font-medium">Awaiting customer authorization</span>
              </div>
              <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm col-span-2 lg:col-span-1">
                <div className="text-[10px] font-bold text-gray-400 uppercase mb-1">Transaction Count</div>
                <div className="text-lg sm:text-2xl font-bold text-purple-600 dark:text-purple-400">{payments.length}</div>
                <span className="text-xs text-purple-500 font-medium">Across all payment channels</span>
              </div>
            </div>

            {/* Filter and Search Bar */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-slate-800 flex flex-col md:flex-row gap-4 justify-between items-center">
                <div className="flex bg-gray-50 dark:bg-slate-800 p-1 rounded-lg border dark:border-slate-700">
                  {['All', 'Paid', 'Pending', 'Failed'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveStatusFilter(tab)}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                        activeStatusFilter === tab
                          ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-300 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Policy or Reference..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  />
                </div>
              </div>

              {/* Ledger Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-50 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 uppercase font-semibold border-b dark:border-slate-800">
                    <tr>
                      <th className="p-3.5">Payment ID</th>
                      <th className="p-3.5">Policy / Instance</th>
                      <th className="p-3.5">Type</th>
                      <th className="p-3.5">Amount</th>
                      <th className="p-3.5">Status</th>
                      <th className="p-3.5">Date & Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-gray-400">
                          <RefreshCw className="animate-spin inline mr-2" size={16} /> Loading transaction ledger...
                        </td>
                      </tr>
                    ) : filteredPayments.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-gray-400">
                          No transactions found matching criteria.
                        </td>
                      </tr>
                    ) : (
                      filteredPayments.map((p) => (
                        <tr key={p.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/40 transition">
                          <td className="p-3.5 font-mono font-semibold text-gray-900 dark:text-white">{p.id}</td>
                          <td className="p-3.5 font-mono text-purple-600 dark:text-purple-400">{p.policy}</td>
                          <td className="p-3.5 text-gray-600 dark:text-gray-300">{p.type}</td>
                          <td className="p-3.5 font-mono font-bold text-gray-900 dark:text-white">{p.amount}</td>
                          <td className="p-3.5">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                p.status === 'Paid'
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                                  : p.status === 'Pending'
                                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                                  : 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
                              }`}
                            >
                              {p.status}
                            </span>
                          </td>
                          <td className="p-3.5 text-gray-500 dark:text-gray-400">{p.dueDate}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: GATEWAY SANDBOX & SIMULATOR */}
        {mainTab === 'sandbox' && (
          <div className="space-y-6">
            {/* Gateway Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* PayChangu */}
              <div
                onClick={() => handleProviderChange(1)}
                className={`cursor-pointer rounded-2xl p-5 border transition-all ${
                  selectedProvider === 1
                    ? 'border-purple-600 bg-purple-50/20 dark:bg-purple-950/20 shadow-md ring-2 ring-purple-500/20'
                    : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Zap className="text-purple-600" size={20} />
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white">PayChangu Aggregator</h3>
                  </div>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 rounded text-[10px] font-bold">
                    ACTIVE SANDBOX
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Unified card, Airtel Money & TNM Mpamba hosted checkout and direct server callbacks.
                </p>
                <div className="text-[11px] font-mono text-gray-400 truncate">
                  Endpoint: {gatewayStatuses?.payChangu.baseUrl || 'https://api.paychangu.com'}
                </div>
              </div>

              {/* Airtel Money */}
              <div
                onClick={() => handleProviderChange(2)}
                className={`cursor-pointer rounded-2xl p-5 border transition-all ${
                  selectedProvider === 2
                    ? 'border-purple-600 bg-purple-50/20 dark:bg-purple-950/20 shadow-md ring-2 ring-purple-500/20'
                    : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Smartphone className="text-red-600" size={20} />
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white">Airtel Money OpenAPI</h3>
                  </div>
                  <span className="px-2 py-0.5 bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300 rounded text-[10px] font-bold">
                    C2B USSD PUSH
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Direct C2B push prompt to Malawian handsets (+26599/98) with async callback.
                </p>
                <div className="text-[11px] font-mono text-gray-400 truncate">
                  Endpoint: {gatewayStatuses?.airtelMoney.baseUrl || 'https://openapiuat.airtel.africa'}
                </div>
              </div>

              {/* TNM Mpamba */}
              <div
                onClick={() => handleProviderChange(3)}
                className={`cursor-pointer rounded-2xl p-5 border transition-all ${
                  selectedProvider === 3
                    ? 'border-purple-600 bg-purple-50/20 dark:bg-purple-950/20 shadow-md ring-2 ring-purple-500/20'
                    : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Smartphone className="text-emerald-600" size={20} />
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white">TNM Mpamba OpenAPI</h3>
                  </div>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 rounded text-[10px] font-bold">
                    STK PROMPT
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Direct wallet authorization prompt to TNM subscribers (+26588) with HMAC signatures.
                </p>
                <div className="text-[11px] font-mono text-gray-400 truncate">
                  Endpoint: {gatewayStatuses?.tnmMpamba.baseUrl || 'https://api.tnm.co.mw/mpamba/v1'}
                </div>
              </div>
            </div>

            {/* Sandbox Simulation Interactive Console */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column: Form Controls & Action Triggers */}
              <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-5">
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Play className="text-purple-600" size={18} />
                    Interactive Sandbox Testing Console
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Simulate customer checkout initiation, USSD mobile push prompts, and async webhook callbacks.
                  </p>
                </div>

                <div className="space-y-4 text-xs">
                  <div>
                    <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Target Policy Number</label>
                    <input
                      type="text"
                      value={testPolicyNumber}
                      onChange={(e) => setTestPolicyNumber(e.target.value)}
                      className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono text-gray-900 dark:text-white"
                      placeholder="e.g. POL-MOT-2026-AUT-101"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Customer Phone (Malawi)</label>
                      <input
                        type="text"
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono text-gray-900 dark:text-white"
                        placeholder="+265..."
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Amount (MWK)</label>
                      <input
                        type="number"
                        value={testAmount}
                        onChange={(e) => setTestAmount(parseFloat(e.target.value))}
                        className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-3 border-t border-gray-100 dark:border-slate-800 space-y-2">
                    <button
                      onClick={handleInitiateCheckout}
                      disabled={initiatingCheckout}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg shadow-sm transition disabled:opacity-50"
                    >
                      <Smartphone size={16} />
                      {initiatingCheckout ? 'Initiating Gateway Request...' : selectedProvider === 1 ? 'Generate PayChangu Checkout Session' : 'Dispatch USSD / STK Mobile Prompt'}
                    </button>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={handleSimulateWebhook}
                        disabled={simulatingWebhook}
                        className="flex items-center justify-center gap-2 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow-sm transition disabled:opacity-50"
                      >
                        <Send size={15} />
                        {simulatingWebhook ? 'Triggering...' : 'Simulate Webhook Callback'}
                      </button>

                      <button
                        onClick={handleVerifyTransaction}
                        disabled={verifyingPayment}
                        className="flex items-center justify-center gap-2 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-sm transition disabled:opacity-50"
                      >
                        <ShieldCheck size={15} />
                        {verifyingPayment ? 'Verifying...' : 'Verify Status with Gateway'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Checkout Result Card */}
                {checkoutResult && (
                  <div className="p-3.5 bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-gray-900 dark:text-white">Gateway Response</span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${checkoutResult.success ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                        {checkoutResult.success ? 'SUCCESS' : 'FAILED'}
                      </span>
                    </div>
                    <div className="text-gray-600 dark:text-gray-300">{checkoutResult.ussdPromptMessage}</div>
                    <div className="font-mono text-purple-600 text-[11px] truncate">Ref: {checkoutResult.transactionReference}</div>
                    {checkoutResult.checkoutUrl && (
                      <a
                        href={checkoutResult.checkoutUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-purple-600 font-bold hover:underline"
                      >
                        Open PayChangu Hosted Checkout <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column: Live Webhook & Binding Result Inspector */}
              <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <ShieldCheck className="text-emerald-600" size={18} />
                    Live Webhook Execution Inspector
                  </h3>
                  <span className="text-xs text-gray-400 font-mono">HMAC SHA-256 Verified</span>
                </div>

                {webhookResult ? (
                  <div className="space-y-4 text-xs">
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl space-y-2">
                      <div className="flex items-center gap-2 font-bold text-emerald-800 dark:text-emerald-300">
                        <CheckCircle2 size={16} />
                        {webhookResult.message}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-gray-600 dark:text-gray-300 pt-2 border-t border-emerald-200 dark:border-emerald-800/40">
                        <div>Policy Number: <span className="font-mono font-bold text-gray-900 dark:text-white">{webhookResult.policyNumber}</span></div>
                        <div>Cover Note: <span className="font-mono font-bold text-purple-600">{webhookResult.digitalCoverNoteNumber}</span></div>
                        <div>Debit Note: <span className="font-mono font-bold text-emerald-600">{webhookResult.debitNoteNumber}</span></div>
                        <div>Amount Paid: <span className="font-mono font-bold text-gray-900 dark:text-white">MWK {webhookResult.amountPaid?.toLocaleString()}</span></div>
                      </div>
                    </div>

                    {/* Serialized JSON Payload */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-gray-600 dark:text-gray-400">Incoming Webhook Payload:</span>
                        <span className="text-[10px] text-gray-400 font-mono">
                          {webhookResult.simulatedPayload.signatureHeaderName}: {webhookResult.simulatedPayload.signatureHeaderValue?.slice(0, 16)}...
                        </span>
                      </div>
                      <pre className="p-3 bg-gray-900 text-emerald-400 rounded-xl text-[11px] font-mono overflow-x-auto max-h-48 border border-gray-800">
                        {JSON.stringify(JSON.parse(webhookResult.simulatedPayload.serializedJson), null, 2)}
                      </pre>
                    </div>
                  </div>
                ) : verificationResult ? (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl space-y-2 text-xs">
                    <div className="font-bold text-blue-800 dark:text-blue-300">Gateway Status Inquiry Result:</div>
                    <pre className="p-2.5 bg-gray-900 text-blue-300 rounded-lg text-[11px] font-mono overflow-x-auto">
                      {JSON.stringify(verificationResult, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <div className="py-16 text-center text-gray-400 border border-dashed rounded-xl space-y-2">
                    <Send className="mx-auto text-gray-300 dark:text-slate-700" size={32} />
                    <p className="text-xs">No simulation executed yet.</p>
                    <p className="text-[11px] text-gray-400">Click "Simulate Webhook Callback" to watch live policy binding & digital document issuance.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}